/* eslint-disable indent */
import type { Theme } from '@elliemae/ds-system';
import { SimpleTruncatedTooltipText } from '@elliemae/ds-truncated-tooltip-text';
import React, { useCallback } from 'react';
import { LEFT_NAVIGATION_DATA_TESTID } from '../../constants/index.js';
import { ItemRenderer } from '../../exported-related/index.js';
import type { DSLeftNavigationT } from '../../react-desc-prop-types.js';
import { StyledItemLabel, StyledLeftContainer, StyledRightContainer } from '../styled.js';

const notOpenedShadowStyle = (isSelected: boolean) => (theme: Theme) =>
  `
  box-shadow: inset 4px 0 0 0 ${isSelected ? theme.colors.brand[400] : 'transparent'};

  :hover {
    box-shadow: inset 4px 0 0 0 ${isSelected ? theme.colors.brand[400] : theme.colors.brand[300]};
  }

  :active {
    box-shadow: inset 4px 0 0 0 ${theme.colors.brand[400]};
  }
`;

export const ItemLink = (props: DSLeftNavigationT.ComponentProps): JSX.Element => {
  const {
    item,
    item: {
      dsId,
      itemOpts: { label, RightComponent, labelDataTestId, labelOverflow: itemLabelOverflow },
      CollapsedComponent = () => null,
    },
    ctx,
    ctx: {
      leftNavProps,
      leftNavProps: { labelOverflow: leftNavLabelOverflow },
      selectedItem,
      selectedParent,
    },
  } = props;

  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);
  const getOwnerPropsArguments = React.useCallback(() => ({}), []);
  const labelOverflow = itemLabelOverflow || leftNavLabelOverflow;

  const isSelected = selectedItem === dsId || selectedParent === dsId;

  const shadowStyle = useCallback(
    (opened_: boolean) => (theme: Theme) =>
      !opened_ ? notOpenedShadowStyle(isSelected)(theme) : `box-shadow: inset 4px 0 0 0 #52A6EC`,
    [isSelected],
  );

  return (
    <ItemRenderer
      item={{
        ...item,
        itemOpts: {
          ariaLabel: typeof label === 'string' ? label : '',
          ...item.itemOpts,
          selectable: true,
        },
      }}
      shadowStyle={shadowStyle}
    >
      <StyledLeftContainer
        cols={['38px']}
        gutter="1px"
        justifyItems="center"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.LEFT_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {CollapsedComponent && <CollapsedComponent item={item} ctx={ctx} />}
      </StyledLeftContainer>
      <StyledItemLabel
        ml="1px"
        mt="xxxs"
        mb="15px"
        labelOverflow={labelOverflow}
        data-testid={labelDataTestId || LEFT_NAVIGATION_DATA_TESTID.ITEM_LABEL}
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {labelOverflow === 'truncate' ? <SimpleTruncatedTooltipText value={label} placement="bottom-start" /> : label}
      </StyledItemLabel>
      <StyledRightContainer
        mt="xxxs"
        pr="xxs2"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.RIGHT_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {RightComponent && <RightComponent ctx={ctx} item={item} />}
      </StyledRightContainer>
    </ItemRenderer>
  );
};
